// Shared UI primitives (ARCHITECTURE §9). Styling lives in src/styles/base.css (class vocabulary) and ui.css.
import './ui.css';
import { Fragment } from 'preact';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../icons.jsx';
import { dialogEntry, onHistoryBack, popDialogEntry, pushDialogEntry, useRoute } from '../router.js';

export const cx = (...parts) => parts.filter(Boolean).join(' ');

// Minimal subscribe/notify cell for module-level UI state (modal stack, toasts).
function cell(initial) {
  let value = initial;
  const subs = new Set();
  return {
    get: () => value,
    set(next) {
      value = typeof next === 'function' ? next(value) : next;
      for (const fn of subs) fn(value);
    },
    useValue() {
      const [v, setV] = useState(value);
      useEffect(() => {
        subs.add(setV);
        setV(value);
        return () => subs.delete(setV);
      }, []);
      return v;
    },
  };
}

const toOption = (o) => (typeof o === 'object' && o !== null ? o : { value: o, label: String(o) });

// ---------- buttons ----------

// variant: 'primary' | 'secondary' | 'ghost' | 'danger'. Renders an <a> when `href` is given.
export function Button({ variant = 'secondary', size, icon, iconRight, block, href, class: cls, className, type = 'button', children, ...rest }) {
  const classes = cx('btn', `btn-${variant}`, size === 'sm' && 'btn-sm', size === 'lg' && 'btn-lg', block && 'btn-block', cls, className);
  const iconSize = size === 'sm' ? 14 : 16;
  const content = (
    <>
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </>
  );
  if (href) return <a class={classes} href={href} {...rest}>{content}</a>;
  return <button type={type} class={classes} {...rest}>{content}</button>;
}

// Icon-only button; `label` becomes aria-label and the tooltip. variant: 'primary' | 'bordered'.
export function IconButton({ icon, label, size, variant, href, class: cls, className, type = 'button', ...rest }) {
  const classes = cx('icon-btn', size === 'sm' && 'icon-btn-sm', variant && `icon-btn-${variant}`, cls, className);
  const glyph = <Icon name={icon} size={size === 'sm' ? 14 : 16} />;
  if (href) return <a class={classes} href={href} aria-label={label} title={label} {...rest}>{glyph}</a>;
  return <button type={type} class={classes} aria-label={label} title={label} {...rest}>{glyph}</button>;
}

// Two-step inline confirm: the first click arms it, the confirm button runs onConfirm. It stays armed until answered
// (no timeout, A11Y-19); Cancel or Esc disarms and gives focus back to the trigger.
export function ConfirmButton({ children, onConfirm, confirmLabel = 'Confirm', cancelLabel = 'Cancel', prompt, variant = 'secondary', size, icon, class: cls }) {
  const [armed, setArmed] = useState(false);
  const confirmRef = useRef(null);
  const triggerRef = useRef(null);
  const wasArmed = useRef(false);
  useEffect(() => {
    if (armed) confirmRef.current?.focus();
    else if (wasArmed.current) triggerRef.current?.querySelector?.('button')?.focus();
    wasArmed.current = armed;
  }, [armed]);
  if (!armed) {
    return <span ref={triggerRef} class="confirm-trigger"><Button variant={variant} size={size} icon={icon} class={cls} onClick={() => setArmed(true)}>{children}</Button></span>;
  }
  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    setArmed(false);
  };
  return (
    <span class={cx('confirm-inline', cls)} role="group" aria-label={typeof children === 'string' ? children : undefined} onKeyDown={onKeyDown}>
      {prompt && <span class="meta">{prompt}</span>}
      <button type="button" ref={confirmRef} class={cx('btn btn-danger', size === 'sm' && 'btn-sm')} onClick={() => { wasArmed.current = false; setArmed(false); onConfirm?.(); }}>
        {confirmLabel}
      </button>
      <Button variant="ghost" size={size} onClick={() => setArmed(false)}>{cancelLabel}</Button>
    </span>
  );
}

// ---------- status ----------

// DESIGN §2 tones. Batch/stage: To Do neutral · In Progress accent · Review warning · Blocked danger · Done positive.
// Client: Active accent · Paused warning · Completed neutral. Month: Upcoming neutral · Active accent · Completed positive.
const STATUS_TONES = {
  'To Do': 'neutral',
  'Not Started': 'neutral', // pre-v2 data, shown as "To Do"
  'In Progress': 'accent',
  Review: 'warning',
  Blocked: 'danger',
  Done: 'positive',
  Paused: 'warning',
  Upcoming: 'neutral',
};

// kind: 'client' (default) | 'month' | 'batch' | 'stage' — 'Active' and 'Completed' read differently for clients and months.
export function statusTone(status, kind = 'client') {
  if (status === 'Active') return 'accent';
  if (status === 'Completed') return kind === 'month' ? 'positive' : 'neutral';
  return STATUS_TONES[status] || 'neutral';
}

// The label a status is shown with ('Not Started' from older data reads "To Do").
export const statusLabel = (status) => (status === 'Not Started' ? 'To Do' : status);

// Small dot + label (6px radius). Done shows a ✓ instead of the dot. `soft` tints the background with the tone.
export function StatusBadge({ status, kind, tone, soft, class: cls, children }) {
  const t = tone || statusTone(status, kind);
  return (
    <span class={cx('badge', `tone-${t}`, soft && 'badge-soft', cls)}>
      {status === 'Done' && !tone
        ? <Icon name="check" size={12} class="badge-check" />
        : <span class="badge-dot" aria-hidden="true" />}
      {children ?? statusLabel(status)}
    </span>
  );
}

// ---------- form fields ----------

// Wraps a control in a <label>. Use as="div" when the control is not a single labelable element (e.g. Segmented).
export function Field({ label, hint, error, required, as: Tag = 'label', class: cls, children }) {
  return (
    <Tag class={cx('field', cls)}>
      {label && (
        <span class="field-label">
          {label}
          {required && <span class="field-required" aria-hidden="true">*</span>}
        </span>
      )}
      {children}
      {error ? <span class="field-error" role="alert">{error}</span> : hint && <span class="field-hint">{hint}</span>}
    </Tag>
  );
}

// Text-like controls: pass `onValue(value)` for the plain string, or native handlers.
// `inputRef` reaches the DOM element (function components do not receive `ref`).
export function TextInput({ value, onValue, onInput, type = 'text', invalid, inputRef, class: cls, ...rest }) {
  return (
    <input
      ref={inputRef}
      type={type}
      class={cx('input', cls)}
      value={value}
      aria-invalid={invalid ? 'true' : undefined}
      onInput={(e) => { onInput?.(e); onValue?.(e.currentTarget.value); }}
      {...rest}
    />
  );
}

export function TextArea({ value, onValue, onInput, rows = 3, invalid, inputRef, class: cls, ...rest }) {
  return (
    <textarea
      ref={inputRef}
      class={cx('textarea', cls)}
      rows={rows}
      value={value}
      aria-invalid={invalid ? 'true' : undefined}
      onInput={(e) => { onInput?.(e); onValue?.(e.currentTarget.value); }}
      {...rest}
    />
  );
}

// options: strings or { value, label, disabled }. `placeholder` adds a leading empty option.
export function Select({ value, onValue, onChange, options = [], placeholder, invalid, inputRef, class: cls, ...rest }) {
  return (
    <select
      ref={inputRef}
      class={cx('select', cls)}
      value={value}
      aria-invalid={invalid ? 'true' : undefined}
      onChange={(e) => { onChange?.(e); onValue?.(e.currentTarget.value); }}
      {...rest}
    >
      {placeholder != null && <option value="">{placeholder}</option>}
      {options.map(toOption).map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
    </select>
  );
}

// Date-only input; value is 'YYYY-MM-DD' or ''.
export function DateInput({ value, onValue, onInput, invalid, inputRef, class: cls, ...rest }) {
  return (
    <input
      ref={inputRef}
      type="date"
      class={cx('input', cls)}
      value={value}
      aria-invalid={invalid ? 'true' : undefined}
      onInput={(e) => { onInput?.(e); onValue?.(e.currentTarget.value); }}
      {...rest}
    />
  );
}

// ---------- layout ----------

export function EmptyState({ title, description, action, compact, class: cls }) {
  return (
    <div class={cx('empty', compact && 'empty-compact', cls)}>
      {title && <p class="empty-title">{title}</p>}
      {description && <p class="empty-desc">{description}</p>}
      {action && <div class="empty-action">{action}</div>}
    </div>
  );
}

// Initials on a circle tinted by a stable hue derived from the name (DESIGN §2). "Unassigned" / empty → dashed "–".
const AVATAR_HUES = [95, 145, 170, 200, 220, 260, 290, 330, 25, 45];

export function avatarHue(name) {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length];
}

export function initials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

export function Avatar({ name, size = 24, class: cls, title }) {
  const unassigned = !String(name || '').trim() || name === 'Unassigned';
  return (
    <span
      class={cx('avatar', unassigned && 'avatar-empty', cls)}
      style={{ width: `${size}px`, height: `${size}px`, fontSize: `${Math.max(9, Math.round(size * 0.4))}px`, '--av-h': unassigned ? undefined : avatarHue(name) }}
      title={title ?? (name || 'Unassigned')}
      aria-hidden="true"
    >
      {unassigned ? '–' : initials(name)}
    </span>
  );
}

// Opens in a new tab. Without `href` renders a muted, non-interactive label.
export function ExternalLink({ href, children, icon = true, class: cls, ...rest }) {
  if (!href) return <span class={cx('link', cls)} aria-disabled="true">{children}</span>;
  return (
    <a class={cx('link external-link', cls)} href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
      {icon && <Icon name="external" size={12} />}
    </a>
  );
}

// ---------- toasts ----------

const toasts = cell([]);
let toastSeq = 0;

function dismissToast(id) {
  toasts.set((list) => list.filter((t) => t.id !== id));
}

// tone: 'neutral' | 'success' | 'danger'. Auto-dismisses after `duration` ms (default 3 s).
export function toast(message, { tone = 'neutral', duration = 3000 } = {}) {
  const id = ++toastSeq;
  toasts.set((list) => [...list.slice(-2), { id, message: String(message), tone }]);
  setTimeout(() => dismissToast(id), duration);
  return id;
}

const TOAST_ICONS = { success: 'check', danger: 'alert' };

export function ToastHost() {
  const list = toasts.useValue();
  return (
    <div class="toast-host" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} class={cx('toast', `toast-${t.tone}`)} role={t.tone === 'danger' ? 'alert' : 'status'} onClick={() => dismissToast(t.id)}>
          {TOAST_ICONS[t.tone] && <Icon name={TOAST_ICONS[t.tone]} size={15} class="toast-icon" />}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- modals ----------

const modals = cell([]);
let modalSeq = 0;

// openModal((close) => <Modal title="…" onClose={close}>…</Modal>) → returns `close`.
// Keep form state inside a component returned by the render function so it survives re-renders.
// Open modals close automatically when the route path changes.
export function openModal(render) {
  const id = ++modalSeq;
  const close = () => modals.set((list) => list.filter((m) => m.id !== id));
  modals.set((list) => [...list, { id, render, close }]);
  return close;
}

// Closes the top-most modal opened with openModal().
export function closeModal() {
  modals.set((list) => list.slice(0, -1));
}

export function ModalHost() {
  const list = modals.useValue();
  const { path } = useRoute();
  const lastPath = useRef(path);
  useEffect(() => {
    if (path !== lastPath.current) {
      lastPath.current = path;
      if (modals.get().length) modals.set([]);
    }
  }, [path]);
  return list.map((m) => <Fragment key={m.id}>{m.render(m.close)}</Fragment>);
}

const openDialogs = []; // mounted <Modal>s in order; Esc closes the last one
let scrollLocks = 0;
let dialogSeq = 0;
let savedBodyStyle = null;

// Phone Back / history.back() while dialogs are open: the top-most dialog whose entry was left behind asks to close.
let stopHistoryBack = null;
function onDialogHistoryBack() {
  const top = openDialogs[openDialogs.length - 1];
  if (top?.marker && dialogEntry() !== top.marker) top.back();
}

function onGlobalKeyDown(e) {
  if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing || !openDialogs.length) return;
  e.preventDefault();
  openDialogs[openDialogs.length - 1].close();
}

function lockScroll() {
  if (scrollLocks++ > 0) return;
  const { style } = document.body;
  const scrollbar = window.innerWidth - document.documentElement.clientWidth;
  savedBodyStyle = { overflow: style.overflow, paddingRight: style.paddingRight };
  style.overflow = 'hidden';
  if (scrollbar > 0) style.paddingRight = `${scrollbar}px`;
}

function unlockScroll() {
  if (--scrollLocks > 0 || !savedBodyStyle) return;
  Object.assign(document.body.style, savedBodyStyle);
  savedBodyStyle = null;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
}

// Focus [autofocus] / [data-autofocus], else the first form control, else the dialog itself.
function focusInitial(dialog) {
  const target = dialog.querySelector('[autofocus], [data-autofocus]')
    || dialog.querySelector('.modal-body input:not([type="hidden"]):not([disabled]), .modal-body select:not([disabled]), .modal-body textarea:not([disabled])')
    || dialog;
  target.focus({ preventScroll: true });
}

// Shared dialog plumbing for Modal and Drawer: Esc closes the top-most dialog, focus is trapped and restored,
// page scroll is locked. Typed input makes the dialog "dirty": a backdrop click then does nothing, and Esc / ✕ ask
// "Discard changes?" first (DESIGN §4). Explicit Cancel/Save buttons call onClose directly.
export function useDialog(onClose) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const dirty = useRef(false);
  const [confirming, setConfirming] = useState(false);
  closeRef.current = onClose;

  const requestClose = () => {
    if (!closeRef.current) return;
    if (dirty.current) setConfirming(true);
    else closeRef.current();
  };
  const requestRef = useRef(requestClose);
  requestRef.current = requestClose;
  const markerRef = useRef('');
  // Back left this dialog's history entry: close it, or — when there is typed input — put the entry back and ask.
  const onBack = () => {
    if (closeRef.current && !dirty.current) return closeRef.current();
    pushDialogEntry(markerRef.current);
    if (closeRef.current) setConfirming(true);
  };
  const backRef = useRef(onBack);
  backRef.current = onBack;

  // Layout effect: focus moves before the next keystroke can land on the page behind.
  useLayoutEffect(() => {
    const previous = document.activeElement;
    const marker = `d${Date.now().toString(36)}-${++dialogSeq}`;
    markerRef.current = marker;
    const entry = { close: () => requestRef.current(), back: () => backRef.current(), marker: pushDialogEntry(marker) ? marker : '' };
    if (!openDialogs.length) {
      document.addEventListener('keydown', onGlobalKeyDown);
      stopHistoryBack = onHistoryBack(onDialogHistoryBack);
    }
    openDialogs.push(entry);
    lockScroll();
    focusInitial(dialogRef.current);
    return () => {
      openDialogs.splice(openDialogs.indexOf(entry), 1);
      if (!openDialogs.length) {
        document.removeEventListener('keydown', onGlobalKeyDown);
        stopHistoryBack?.();
        stopHistoryBack = null;
      }
      if (entry.marker) popDialogEntry(entry.marker);
      unlockScroll();
      if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
    };
  }, []);

  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const items = focusables(dialogRef.current);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const pointerDownOnBackdrop = useRef(false);
  const backdropProps = {
    onMouseDown: (e) => { pointerDownOnBackdrop.current = e.target === e.currentTarget; },
    onClick: (e) => {
      if (pointerDownOnBackdrop.current && e.target === e.currentTarget && !dirty.current) closeRef.current?.();
      pointerDownOnBackdrop.current = false;
    },
  };
  const markDirty = (e) => { if (e.target && e.target.closest && !e.target.closest('[data-ignore-dirty]')) dirty.current = true; };

  const discardBar = confirming && (
    <div class="discard-bar" role="alert">
      <span>Discard your changes?</span>
      <span class="spacer" />
      <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Keep editing</Button>
      <Button size="sm" variant="danger" onClick={() => { setConfirming(false); closeRef.current?.(); }}>Discard</Button>
    </div>
  );

  return { dialogRef, trapTab, backdropProps, markDirty, requestClose, discardBar };
}

// size: 'sm' | 'md' (default, ~520px) | 'lg' (~720px). With `onSubmit` the dialog is a <form>,
// so Enter submits and a footer <Button type="submit"> works. Full-screen sheet below 768px.
// Use a Modal only when focus is truly required; quick-create forms use Drawer.
export function Modal({ title, onClose, footer, size, onSubmit, class: cls, children }) {
  const { dialogRef, trapTab, backdropProps, markDirty, requestClose, discardBar } = useDialog(onClose);
  const titleId = useId();
  const Tag = onSubmit ? 'form' : 'div';
  const submit = onSubmit ? (e) => { e.preventDefault(); onSubmit(e); } : undefined;

  return (
    <div class="modal-backdrop" {...backdropProps}>
      <Tag
        ref={dialogRef}
        class={cx('modal', size === 'lg' && 'modal-lg', size === 'sm' && 'modal-sm', cls)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={trapTab}
        onInput={markDirty}
        onChange={markDirty}
        onSubmit={submit}
        noValidate={onSubmit ? true : undefined}
      >
        <div class="modal-header">
          <h2 class="modal-title" id={titleId}>{title}</h2>
          {onClose && <IconButton icon="x" label="Close" size="sm" onClick={requestClose} />}
        </div>
        <div class="modal-body">{children}</div>
        {discardBar}
        {footer && <div class="modal-footer">{footer}</div>}
      </Tag>
    </div>
  );
}
